import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { ScopeChooser } from '@/features/search/ScopeChooser';
import { SignalSheet, type SheetSeed } from '@/features/signals/SignalSheet';
import { fetchCorpusStats, fetchFollowedTowns, runSearch, type SearchScope } from '@/lib/api';
import { sayCountries, sayMeetings, sayTowns } from '@/lib/corpus';
import type { CountryCode } from '@/lib/tier';
import { TIERS } from '@/lib/tier';
import { categoryFor } from '@/lib/signals';
import type { BodyType } from '@/lib/types';
import { useTier } from '@/session/SessionProvider';
import './Search.css';

/**
 * Cross-city Search.
 *
 * **Opens blank, with zero filters.** A prior version silently applied defaults — the wrong
 * body type, the wrong state — and quietly returned an incomplete answer. The fix was to
 * default to nothing and state the scope out loud in the badge above the results. Do not
 * reintroduce a default filter, however sensible it looks.
 *
 * Matching is hybrid semantic + keyword: "battery storage pause" has to find "moratorium on
 * energy storage". The empty state says so, so it has to be true.
 */
const STARTER_QUERIES = [
  { query: 'battery storage moratorium', hint: 'Environment · municipal' },
  { query: 'data center rezoning', hint: 'Urban Planning · municipal' },
  { query: 'cell phone ban', hint: 'Education · board of ed' },
  { query: 'interconnection agreement', hint: 'Infrastructure · municipal' },
  { query: 'short-term rental cap', hint: 'Urban Planning · municipal' },
  { query: 'PILOT agreement', hint: 'Finance · municipal' },
];

export function Search() {
  const tier = useTier();
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [country, setCountry] = useState<CountryCode>(TIERS[tier].countries[0] ?? 'US');
  const [regions, setRegions] = useState<string[]>([]);
  const [body, setBody] = useState('all');
  const [category, setCategory] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sheet, setSheet] = useState<SheetSeed | null>(null);

  const statsQuery = useQuery({ queryKey: ['corpus'], queryFn: fetchCorpusStats });
  const townsQuery = useQuery({ queryKey: ['towns', tier], queryFn: fetchFollowedTowns });

  const submitted = query.trim().length > 0;
  const resultsQuery = useQuery({
    queryKey: ['search', query, scope, regions, body, category],
    queryFn: () => runSearch({ q: query, scope, regions, body, category }),
    enabled: submitted,
    placeholderData: (previous) => previous,
  });

  const corpusSummary = useMemo(() => {
    const stats = statsQuery.data;
    if (!stats) return 'every place · every body type';
    return `${sayMeetings(stats)} meetings · ${sayTowns(stats)} towns · every body type`;
  }, [statsQuery.data]);

  const results = resultsQuery.data;
  const filtered = useMemo(() => {
    if (!results) return [];
    return results.results.filter(
      (r) =>
        (body === 'all' || r.body === body) &&
        (category === 'all' || categoryFor(r.eventType) === category),
    );
  }, [results, body, category]);

  const activeFilters = (body === 'all' ? 0 : 1) + (category === 'all' ? 0 : 1);

  return (
    <div className="search">
      <div className="search__top">
        <label className="search__field">
          <span className="u-sr-only">Search meetings</span>
          <Icon name="search" />
          <input
            type="search"
            value={query}
            placeholder="Search every meeting we hold"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQuery('');
            }}
          />
          {query && (
            <button type="button" className="search__clear" onClick={() => setQuery('')}>
              Clear
            </button>
          )}
        </label>

        <ScopeChooser
          scope={scope}
          onScopeChange={setScope}
          country={country}
          onCountryChange={setCountry}
          regions={regions}
          onRegionsChange={setRegions}
          followedTownNames={(townsQuery.data ?? []).map((t) => t.name)}
          corpusSummary={corpusSummary}
        />
      </div>

      {!submitted ? (
        <div className="search__empty">
          <h1 className="search__emptyTitle">Search across every place</h1>
          <p className="search__emptyBody">
            Meaning, not just words — “battery storage pause” finds “moratorium on energy
            storage.” Start with one of these:
          </p>
          <ul className="search__starters">
            {STARTER_QUERIES.map((starter) => (
              <li key={starter.query}>
                <button
                  type="button"
                  className="search__starter"
                  onClick={() => setQuery(starter.query)}
                >
                  <span className="search__starterQuery">{starter.query}</span>
                  <span className="search__starterHint">{starter.hint}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="search__body">
          <div className="search__resultsHead">
            <p className="search__count">
              {resultsQuery.isLoading
                ? 'Searching…'
                : `${plural(results?.total ?? 0, 'result')} across ${plural(results?.cities ?? 0, 'city', 'cities')}`}
            </p>
            <div className="search__headActions">
              <button
                type="button"
                className="search__filterToggle"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
              >
                Filters{activeFilters > 0 ? ` · ${activeFilters}` : ''}
              </button>
              {/* Captures the current query AND filters into a new Signal — the same creation
                  sheet opened from Signal Feed and Topic Trends. */}
              <button
                type="button"
                className="search__save"
                onClick={() =>
                  // Carries the query AND the current filters into the sheet, which is the
                  // whole point of saving from here rather than rebuilding it by hand.
                  setSheet({
                    name: query,
                    terms: query,
                    scope: scope === 'mine' ? 'mytowns' : scope === 'pick' ? 'states' : 'corpus',
                    bodies: body === 'all' ? ['municipal'] : [body as BodyType],
                    categories: category === 'all' ? [] : [category],
                  })
                }
              >
                <Icon name="signals" /> Save as signal
              </button>
            </div>
          </div>

          <div className="search__panes">
            <aside className="search__facets" data-open={filtersOpen}>
              <Facet
                title="Body type"
                counts={results?.facets.body ?? {}}
                value={body}
                onChange={setBody}
                labels={{ municipal: 'Municipal', boe: 'Board of Ed' }}
              />
              <Facet
                title="Category"
                counts={results?.facets.category ?? {}}
                value={category}
                onChange={setCategory}
              />
              <Facet
                title="State"
                counts={results?.facets.region ?? {}}
                value="all"
                onChange={() => {
                  /* Region filtering runs through the scope chooser above, not here. */
                }}
                readOnly
              />
            </aside>

            <div className="search__results">
              {filtered.length === 0 && !resultsQuery.isLoading && (
                <p className="search__none">
                  Nothing matched “{query}” in this scope. Try widening it above.
                </p>
              )}
              {filtered.map((result) => (
                <article key={result.id} className="search__result">
                  <div className="search__resultHead">
                    <BodyBadge body={result.body} short />
                    <strong className="search__city">
                      {result.city}, {result.region}
                    </strong>
                    <span className="search__date">{result.date}</span>
                  </div>
                  <p className="search__passage">
                    {result.passage.before}
                    <mark className="feed__mark">{result.passage.hit}</mark>
                    {result.passage.after}
                  </p>
                  {result.entities.length > 0 && (
                    <ul className="search__entities">
                      {result.entities.map((entity) => (
                        <li key={entity}>{entity}</li>
                      ))}
                    </ul>
                  )}
                  {result.num && <span className="search__num">{result.num}</span>}
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {sheet && (
        <SignalSheet
          seed={sheet}
          followedTownCount={(townsQuery.data ?? []).length}
          corpusSummary={
            statsQuery.data
              ? `${sayTowns(statsQuery.data)} towns · ${sayCountries(statsQuery.data)} countries`
              : 'every place'
          }
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

/** "1 result" / "2 results" — a count next to a bare plural noun reads as a bug. */
function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

function Facet({
  title,
  counts,
  value,
  onChange,
  labels = {},
  readOnly = false,
}: {
  title: string;
  counts: Record<string, number>;
  value: string;
  onChange: (value: string) => void;
  labels?: Record<string, string>;
  readOnly?: boolean;
}) {
  const keys = Object.keys(counts).sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0));
  if (keys.length === 0) return null;

  return (
    <section className="search__facet">
      <h2 className="u-meta">{title}</h2>
      <ul>
        {keys.map((key) => (
          <li key={key}>
            <button
              type="button"
              className="search__facetRow"
              data-active={value === key}
              disabled={readOnly}
              onClick={() => onChange(value === key ? 'all' : key)}
            >
              <span>{labels[key] ?? key}</span>
              <span className="search__facetCount">{counts[key]}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
